import { type FC } from 'react';

// MUI
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

// screen
import { type LayoutProps } from 'modules/map/components/daily-dish/types';
import { getFontFamily } from 'modules/map/components/daily-dish/templates/utils';
import WeekdayTitle from 'modules/map/components/daily-dish/templates/components/weekday/WeekdayTitle';
import { WeekdayTitlePosition } from 'modules/map/components/daily-dish/constants';

/*
 * Layout 1: Two Columns Left (50% width, left-aligned)
 */
export const TwoColumnsLayout: FC<LayoutProps> = (props) => {
  const { days, settings, maxCount, getDishes } = props;

  const isLeftAlign = settings.general.layoutAlign === 'left';

  /*
   * Khoảng cách lề khi căn trái hoặc phải
   */
  const sideMargin = 100;

  const width = '430px';
  const height = '260px';

  return (
    <Grid
      container
      spacing={1}
      mt={0}
      sx={{
        width: '50%',
        marginLeft: isLeftAlign ? `${sideMargin}px` : 'auto',
        marginRight: isLeftAlign ? 'auto' : `${sideMargin}px`,
        rowGap: 4,
        mb: 4,
      }}
    >
      {days.map(({ weekDay, dishes, afternoons }, index) => {
        const lunchDishes = getDishes(dishes, maxCount.lunch);
        const afternoonDishes = getDishes(afternoons, maxCount.afternoon) as (string[] | null)[];
        const cardColor = settings.menu.weekday[weekDay].borderColor;

        // Special handling for 5 items (1-2-2 pattern)
        const isFiveItems = days.length === 5;
        const isFirstItem = index === 0;
        const itemColumns =
          isFiveItems && isFirstItem ? { mobile: 12, tablet: 12 } : { mobile: 12, tablet: 6 };

        return (
          <Grid
            key={`${weekDay}-${index}`}
            item
            mobile={itemColumns.mobile}
            tablet={itemColumns.tablet}
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'stretch',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width,
                height,
                border: `${settings.menu.borderWidth}px solid ${cardColor}`,
                borderRadius: `${settings.menu.borderRadius}px`,
                backgroundColor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  flex: 1,
                  py: 3,
                  borderRadius: `${settings.menu.borderRadius}px`,
                }}
              >
                <Typography
                  component={'div'}
                  variant="caption"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    fontSize: `${settings.font.content.fontSize}px`,
                    lineHeight: 1.4,
                    color: settings.font.content.fontColor,
                    fontFamily: getFontFamily(settings.font.content.fontFamilyId),
                  }}
                >
                  {lunchDishes.map((dish, idx) => (
                    <Typography
                      variant="inherit"
                      key={idx}
                      sx={{
                        userSelect: 'none',
                        color: 'inherit',
                        textAlign: 'center',
                      }}
                    >
                      {dish ? dish : <>&nbsp;</>}
                    </Typography>
                  ))}

                  {afternoons.length > 0 && (
                    <Divider sx={{ borderBottom: 1, borderColor: cardColor }} />
                  )}

                  {afternoonDishes.map((meal, idx) => {
                    if (!meal) return <>&nbsp;</>;

                    const dishName = (meal[0] || '').replace(/\d\./, '');
                    const manufacturer = meal[1] || '';

                    return (
                      <Typography
                        key={idx}
                        sx={{
                          userSelect: 'none',
                          color: 'inherit',
                          textAlign: 'center',
                        }}
                        variant="inherit"
                      >
                        {`${dishName} ${manufacturer}`}
                      </Typography>
                    );
                  })}
                </Typography>
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top:
                    settings.menu.weekdayTitlePosition === WeekdayTitlePosition.TOP
                      ? '-28px'
                      : 'auto',
                  bottom:
                    settings.menu.weekdayTitlePosition === WeekdayTitlePosition.BOTTOM
                      ? '-28px'
                      : 'auto',
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                <WeekdayTitle weekDay={weekDay} settings={settings} layoutId={1} />
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default TwoColumnsLayout;
